// The pictures of measuring with informal units: containers and the cups
// that fill them, a thing with paper clips or cubes laid under it, and a
// two-pan balance. Plain React SVG, like practice-measure.tsx.
//
// None of them prints a number or a unit. The only words are the names of
// the containers and of the things on a balance; the cups, clips and cubes
// are drawn one by one, in fives or in two shades, for the child to count.

import type { ReactNode } from 'react';
import {
  INFORMAL_LABEL_SIZE,
  balancePicture,
  cupsPicture,
  describeInformalMeasure,
  unitLinePicture,
  type BalanceItem,
  type BalancePan,
  type BalanceSpec,
  type CupsColumn,
  type CupsSpec,
  type InformalMeasureSpec,
  type LineObject,
  type UnitLineSpec,
} from './practice-informal-measure-data';

const INK = '#2d4a3e';
const WATER_FILL = '#9cc9e8';
const WATER_LINE = '#2f6f9f';
const CARD_FILL = '#f4f8f4';
const GLASS_FILL = '#ffffff';
// Two shades that take turns, so units laid end to end stand apart.
const UNIT_SHADES = ['#6fa8dc', '#f2c94c'] as const;
const CLIP_SHADES = ['#3d6fa8', '#c0632b'] as const;

function n(value: number): string {
  return String(Number(value.toFixed(2)));
}

function points(list: [number, number][]): string {
  return list.map(([x, y]) => `${n(x)},${n(y)}`).join(' ');
}

function Frame({
  spec,
  width,
  height,
  grow = 1.3,
  children,
}: {
  spec: InformalMeasureSpec;
  width: number;
  height: number;
  grow?: number;
  children: ReactNode;
}) {
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeInformalMeasure(spec)}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(width * grow))}px` }}
      >
        {children}
      </svg>
    </figure>
  );
}

// The inside of each container, as one closed outline.
function containerOutline(column: CupsColumn): string {
  const { x, y, width: w, height: h } = column.box;
  const bottom = y + h;
  switch (column.container) {
    case 'bowl':
      return `M ${n(x)} ${n(y)} Q ${n(x)} ${n(bottom)} ${n(x + w / 2)} ${n(bottom)} Q ${n(x + w)} ${n(bottom)} ${n(x + w)} ${n(y)} Z`;
    case 'glass':
      return `M ${n(x)} ${n(y)} L ${n(x + w * 0.12)} ${n(bottom)} L ${n(x + w * 0.88)} ${n(bottom)} L ${n(x + w)} ${n(y)} Z`;
    case 'bucket':
      return `M ${n(x)} ${n(y)} L ${n(x + w * 0.1)} ${n(bottom)} L ${n(x + w * 0.9)} ${n(bottom)} L ${n(x + w)} ${n(y)} Z`;
    case 'bottle': {
      const neckLeft = x + w * 0.32;
      const neckRight = x + w * 0.68;
      return `M ${n(neckLeft)} ${n(y)} L ${n(neckLeft)} ${n(y + h * 0.2)} L ${n(x)} ${n(y + h * 0.32)} L ${n(x)} ${n(bottom)} L ${n(x + w)} ${n(bottom)} L ${n(x + w)} ${n(y + h * 0.32)} L ${n(neckRight)} ${n(y + h * 0.2)} L ${n(neckRight)} ${n(y)} Z`;
    }
    case 'vase':
      return `M ${n(x + w * 0.2)} ${n(y)} L ${n(x + w * 0.35)} ${n(y + h * 0.2)} C ${n(x - w * 0.15)} ${n(y + h * 0.4)} ${n(x)} ${n(bottom)} ${n(x + w * 0.25)} ${n(bottom)} L ${n(x + w * 0.75)} ${n(bottom)} C ${n(x + w)} ${n(bottom)} ${n(x + w * 1.15)} ${n(y + h * 0.4)} ${n(x + w * 0.65)} ${n(y + h * 0.2)} L ${n(x + w * 0.8)} ${n(y)} Z`;
    case 'jar':
      return `M ${n(x)} ${n(y + 8)} L ${n(x)} ${n(bottom)} L ${n(x + w)} ${n(bottom)} L ${n(x + w)} ${n(y + 8)} Z`;
    case 'pot':
      return `M ${n(x)} ${n(y)} L ${n(x)} ${n(bottom)} L ${n(x + w)} ${n(bottom)} L ${n(x + w)} ${n(y)} Z`;
  }
}

function ContainerShape({ column }: { column: CupsColumn }) {
  const { x, y, width: w, height: h } = column.box;
  // Made from the drawing itself, so two clips share a name only when they
  // are the same shape, even across pictures drawn apart.
  const clip = `water-${column.container}-${[x, y, w, h, column.waterTop]
    .map(n)
    .join('-')}`.replace(/[.]/g, '_');
  const outline = containerOutline(column);
  return (
    <g data-container={column.container}>
      <defs>
        <clipPath id={clip}>
          <path d={outline} />
        </clipPath>
      </defs>
      <path d={outline} fill={GLASS_FILL} />
      <rect
        data-water="true"
        x={x - 10}
        y={column.waterTop}
        width={w + 20}
        height={y + h - column.waterTop}
        fill={WATER_FILL}
        clipPath={`url(#${clip})`}
      />
      <line
        x1={x - 10}
        y1={column.waterTop}
        x2={x + w + 10}
        y2={column.waterTop}
        stroke={WATER_LINE}
        strokeWidth="1.5"
        clipPath={`url(#${clip})`}
      />
      {column.marks.map((markY) => (
        <line
          key={markY}
          data-mark="true"
          x1={x + w - 14}
          y1={markY}
          x2={x + w}
          y2={markY}
          stroke={INK}
          strokeWidth="2"
        />
      ))}
      <path
        d={outline}
        fill="none"
        stroke={INK}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {column.container === 'jar' && (
        <rect x={x - 3} y={y} width={w + 6} height="8" rx="2" fill={INK} />
      )}
      {column.container === 'bucket' && (
        <path
          d={`M ${n(x + 4)} ${n(y)} Q ${n(x + w / 2)} ${n(y - 30)} ${n(x + w - 4)} ${n(y)}`}
          fill="none"
          stroke={INK}
          strokeWidth="2"
        />
      )}
      {/* The handles sit at the rim, clear of the marks inside. */}
      {column.container === 'pot' &&
        [x - 8, x + w].map((left) => (
          <rect
            key={left}
            x={left}
            y={y + 2}
            width="8"
            height="6"
            rx="2"
            fill={INK}
          />
        ))}
    </g>
  );
}

// One cup, or one spoon, of water.
function CupIcon({
  unit,
  x,
  y,
  size,
}: {
  unit: CupsSpec['unit'];
  x: number;
  y: number;
  size: number;
}) {
  const s = size;
  if (unit === 'spoon') {
    return (
      <g data-unit="spoon">
        <ellipse
          cx={x + s * 0.34}
          cy={y + s * 0.5}
          rx={s * 0.34}
          ry={s * 0.26}
          fill={WATER_FILL}
          stroke={INK}
          strokeWidth="1.3"
        />
        <line
          x1={x + s * 0.64}
          y1={y + s * 0.5}
          x2={x + s * 0.9}
          y2={y + s * 0.5}
          stroke={INK}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </g>
    );
  }
  return (
    <g data-unit="cup">
      <path
        d={`M ${n(x + s * 0.75)} ${n(y + s * 0.35)} Q ${n(x + s * 1.02)} ${n(y + s * 0.4)} ${n(x + s * 0.7)} ${n(y + s * 0.75)}`}
        fill="none"
        stroke={INK}
        strokeWidth="1.3"
      />
      <polygon
        points={points([
          [x + s * 0.05, y + s * 0.15],
          [x + s * 0.18, y + s],
          [x + s * 0.65, y + s],
          [x + s * 0.78, y + s * 0.15],
        ])}
        fill={WATER_FILL}
        stroke={INK}
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </g>
  );
}

function CupsView({ spec }: { spec: CupsSpec }) {
  const picture = cupsPicture(spec);
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      {picture.columns.map((column, index) => (
        <g key={index} data-column={index + 1}>
          <rect
            {...column.card}
            rx="10"
            fill={CARD_FILL}
            stroke={INK}
            strokeWidth="1"
          />
          <ContainerShape column={column} />
          <text
            data-name="true"
            x={column.name.x}
            y={column.name.y}
            dy="0.35em"
            textAnchor="middle"
            fill={INK}
            fontSize={INFORMAL_LABEL_SIZE}
            fontWeight="700"
          >
            {column.container}
          </text>
          {column.cups.map((cup, at) => (
            <CupIcon
              key={at}
              unit={spec.unit}
              x={cup.x}
              y={cup.y}
              size={picture.cupSize}
            />
          ))}
        </g>
      ))}
    </Frame>
  );
}

// The thing being measured, lying from one x to the other.
function LineObjectShape({
  object,
  from,
  to,
  y,
  height: h,
}: {
  object: LineObject;
  from: number;
  to: number;
  y: number;
  height: number;
}) {
  const line = {
    stroke: INK,
    strokeWidth: 1.5,
    strokeLinejoin: 'round' as const,
  };
  const middle = y + h / 2;
  switch (object) {
    case 'pencil': {
      // A short pencil keeps its rubber, body and point in proportion.
      const tip = to - Math.min(h, (to - from) * 0.3);
      const rubber = Math.min(10, (to - from) * 0.15);
      return (
        <g data-object="pencil">
          <rect
            x={from}
            y={y}
            width={rubber}
            height={h}
            fill="#e98bbf"
            {...line}
          />
          <rect
            x={from + rubber}
            y={y}
            width={tip - from - rubber}
            height={h}
            fill="#f2c94c"
            {...line}
          />
          <polygon
            points={points([
              [tip, y],
              [to, middle],
              [tip, y + h],
            ])}
            fill="#f6e3c0"
            {...line}
          />
        </g>
      );
    }
    case 'crayon': {
      const tip = to - h * 0.7;
      return (
        <g data-object="crayon">
          <rect
            x={from}
            y={y}
            width={tip - from}
            height={h}
            rx="3"
            fill="#e0605a"
            {...line}
          />
          <rect
            x={from + (tip - from) * 0.25}
            y={y}
            width={(tip - from) * 0.5}
            height={h}
            fill="#f4f8f4"
            {...line}
          />
          <polygon
            points={points([
              [tip, y + h * 0.15],
              [to, middle],
              [tip, y + h * 0.85],
            ])}
            fill="#e0605a"
            {...line}
          />
        </g>
      );
    }
    case 'ribbon':
      return (
        <g data-object="ribbon">
          <polygon
            points={points([
              [from, y],
              [to, y],
              [to - 7, middle],
              [to, y + h],
              [from, y + h],
              [from + 7, middle],
            ])}
            fill="#7b5ea7"
            {...line}
          />
        </g>
      );
    case 'rope':
      return (
        <g data-object="rope">
          <rect
            x={from}
            y={y}
            width={to - from}
            height={h}
            rx={h / 2}
            fill="#c9a36b"
            {...line}
          />
          {Array.from(
            { length: Math.max(0, Math.floor((to - from - h) / 8)) },
            (_, at) => (
              <line
                key={at}
                x1={from + h / 2 + at * 8}
                y1={y + h - 2}
                x2={from + h / 2 + at * 8 + 6}
                y2={y + 2}
                stroke={INK}
                strokeWidth="0.8"
              />
            ),
          )}
        </g>
      );
    case 'book':
      return (
        <g data-object="book">
          <rect
            x={from}
            y={y}
            width={to - from}
            height={h}
            rx="2"
            fill="#6aa86a"
            {...line}
          />
          <rect x={from} y={y} width="8" height={h} fill="#3f7a3f" {...line} />
        </g>
      );
    case 'straw':
      return (
        <g data-object="straw">
          <rect
            x={from}
            y={y}
            width={to - from}
            height={h}
            fill="#ffffff"
            {...line}
          />
          {Array.from({ length: Math.floor((to - from) / 10) }, (_, at) => (
            <rect
              key={at}
              x={from + at * 10 + 2}
              y={y + 1}
              width="4"
              height={h - 2}
              fill="#e0605a"
            />
          ))}
        </g>
      );
  }
}

function UnitLineView({ spec }: { spec: UnitLineSpec }) {
  const picture = unitLinePicture(spec);
  const { object } = picture;
  const bottom = Math.max(...picture.units.map((unit) => unit.y + unit.height));
  return (
    <Frame spec={spec} width={picture.width} height={picture.height} grow={1.4}>
      <LineObjectShape object={spec.object} {...object} />
      {[object.from, object.to].map((x) => (
        <line
          key={x}
          data-guide="true"
          x1={x}
          y1={object.y + object.height}
          x2={x}
          y2={bottom + 2}
          stroke={INK}
          strokeWidth="1"
          strokeDasharray="3 3"
        />
      ))}
      {picture.units.map((unit, at) =>
        spec.unit === 'cube' ? (
          <rect
            key={at}
            data-unit="cube"
            x={unit.x}
            y={unit.y}
            width={unit.length}
            height={unit.height}
            fill={UNIT_SHADES[at % 2]}
            stroke={INK}
            strokeWidth="1.5"
          />
        ) : (
          // A paper clip: a long loop with a smaller loop inside it.
          <g key={at} data-unit="paper_clip">
            <rect
              x={unit.x + 1}
              y={unit.y + 1}
              width={unit.length - 2}
              height={unit.height - 2}
              rx={(unit.height - 2) / 2}
              fill="none"
              stroke={CLIP_SHADES[at % 2]}
              strokeWidth="2"
            />
            <rect
              x={unit.x + unit.height * 0.45}
              y={unit.y + unit.height * 0.3}
              width={unit.length - unit.height * 1.1}
              height={unit.height * 0.4}
              rx={unit.height * 0.2}
              fill="none"
              stroke={CLIP_SHADES[at % 2]}
              strokeWidth="1.5"
            />
          </g>
        ),
      )}
    </Frame>
  );
}

// One thing on a balance pan, filling a square of side `size` whose top
// left corner is (x, y).
function PanThing({
  item,
  x,
  y,
  size: s,
  index,
}: {
  item: BalanceItem;
  x: number;
  y: number;
  size: number;
  index: number;
}) {
  const cx = x + s / 2;
  const cy = y + s / 2;
  const line = {
    stroke: INK,
    strokeWidth: 1.4,
    strokeLinejoin: 'round' as const,
  };
  switch (item) {
    case 'cube':
      return (
        <rect
          data-item="cube"
          x={x}
          y={y}
          width={s}
          height={s}
          fill={UNIT_SHADES[index % 2]}
          {...line}
        />
      );
    case 'marble':
      return (
        <g data-item="marble">
          <circle cx={cx} cy={cy} r={s * 0.46} fill="#7b5ea7" {...line} />
          <circle
            cx={cx - s * 0.15}
            cy={cy - s * 0.15}
            r={s * 0.1}
            fill="#ffffff"
          />
        </g>
      );
    case 'apple':
      return (
        <g data-item="apple">
          <circle
            cx={cx}
            cy={cy + s * 0.06}
            r={s * 0.4}
            fill="#e0605a"
            {...line}
          />
          <line
            x1={cx}
            y1={cy - s * 0.32}
            x2={cx + s * 0.05}
            y2={y + s * 0.02}
            {...line}
          />
          <ellipse
            cx={cx + s * 0.18}
            cy={y + s * 0.1}
            rx={s * 0.12}
            ry={s * 0.06}
            fill="#6aa86a"
          />
        </g>
      );
    case 'teddy':
      return (
        <g data-item="teddy">
          <circle
            cx={cx - s * 0.2}
            cy={y + s * 0.12}
            r={s * 0.1}
            fill="#b5835a"
            {...line}
          />
          <circle
            cx={cx + s * 0.2}
            cy={y + s * 0.12}
            r={s * 0.1}
            fill="#b5835a"
            {...line}
          />
          <circle
            cx={cx}
            cy={y + s * 0.65}
            r={s * 0.33}
            fill="#b5835a"
            {...line}
          />
          <circle
            cx={cx}
            cy={y + s * 0.26}
            r={s * 0.21}
            fill="#b5835a"
            {...line}
          />
        </g>
      );
    case 'car':
      return (
        <g data-item="car">
          <polygon
            points={points([
              [x + s * 0.25, y + s * 0.5],
              [x + s * 0.35, y + s * 0.28],
              [x + s * 0.68, y + s * 0.28],
              [x + s * 0.78, y + s * 0.5],
            ])}
            fill="#8fc0e8"
            {...line}
          />
          <rect
            x={x + s * 0.04}
            y={y + s * 0.5}
            width={s * 0.92}
            height={s * 0.26}
            rx="3"
            fill="#c0392b"
            {...line}
          />
          {[0.27, 0.73].map((at) => (
            <circle
              key={at}
              cx={x + s * at}
              cy={y + s * 0.8}
              r={s * 0.11}
              fill={INK}
            />
          ))}
        </g>
      );
    case 'book':
      return (
        <g data-item="book">
          <rect
            x={x + s * 0.02}
            y={y + s * 0.55}
            width={s * 0.96}
            height={s * 0.4}
            rx="2"
            fill="#6aa86a"
            {...line}
          />
          <line
            x1={x + s * 0.1}
            y1={y + s * 0.75}
            x2={x + s * 0.9}
            y2={y + s * 0.75}
            stroke="#ffffff"
            strokeWidth="1.5"
          />
        </g>
      );
    case 'ball':
      return (
        <g data-item="ball">
          <circle cx={cx} cy={cy} r={s * 0.44} fill="#6fa8dc" {...line} />
          <path
            d={`M ${n(cx - s * 0.44)} ${n(cy)} Q ${n(cx)} ${n(cy + s * 0.25)} ${n(cx + s * 0.44)} ${n(cy)}`}
            fill="none"
            stroke={INK}
            strokeWidth="1"
          />
        </g>
      );
    case 'pencil':
      return (
        <g data-item="pencil">
          <polygon
            points={points([
              [x, y + s * 0.72],
              [x + s * 0.78, y + s * 0.72],
              [x + s, y + s * 0.84],
              [x + s * 0.78, y + s * 0.96],
              [x, y + s * 0.96],
            ])}
            fill="#f2c94c"
            {...line}
          />
        </g>
      );
  }
}

function Pan({ pan, side }: { pan: BalancePan; side: 'left' | 'right' }) {
  const half = pan.width / 2;
  return (
    <g data-pan={side} data-pan-y={pan.y}>
      {pan.pieces.map((piece, index) => (
        <PanThing
          key={index}
          item={piece.item}
          x={piece.x}
          y={piece.y}
          size={piece.size}
          index={index}
        />
      ))}
      <polygon
        points={points([
          [pan.x - half, pan.y],
          [pan.x + half, pan.y],
          [pan.x + half - 10, pan.y + 9],
          [pan.x - half + 10, pan.y + 9],
        ])}
        fill="#d9d9d9"
        stroke={INK}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {pan.names.map((name) => (
        <text
          key={name.text}
          data-name="true"
          x={name.x}
          y={name.y}
          dy="0.35em"
          textAnchor="middle"
          fill={INK}
          fontSize={INFORMAL_LABEL_SIZE}
        >
          {name.text}
        </text>
      ))}
    </g>
  );
}

function BalanceView({ spec }: { spec: BalanceSpec }) {
  const picture = balancePicture(spec);
  const { pivot, beam, base } = picture;
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      <rect
        x={pivot.x - 5}
        y={pivot.y}
        width="10"
        height={base.y - pivot.y}
        fill={INK}
      />
      <polygon
        points={points([
          [pivot.x - 46, base.y + 14],
          [pivot.x - 34, base.y],
          [pivot.x + 34, base.y],
          [pivot.x + 46, base.y + 14],
        ])}
        fill={INK}
      />
      <line
        data-beam={picture.angle}
        x1={beam.x1}
        y1={beam.y1}
        x2={beam.x2}
        y2={beam.y2}
        stroke={INK}
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle
        cx={pivot.x}
        cy={pivot.y}
        r="7"
        fill="#ffffff"
        stroke={INK}
        strokeWidth="2.5"
      />
      <Pan pan={picture.left} side="left" />
      <Pan pan={picture.right} side="right" />
    </Frame>
  );
}

export function InformalMeasureView({
  measure,
}: {
  measure: InformalMeasureSpec;
}) {
  switch (measure.type) {
    case 'cups':
      return <CupsView spec={measure} />;
    case 'unit_line':
      return <UnitLineView spec={measure} />;
    case 'balance':
      return <BalanceView spec={measure} />;
  }
}
